import type { RecurrenceRule } from '../../../utils/rrule.ts';

/**
 * GROW-99: the client edits a recurrence rule as plain data, which is why no RRULE parser or
 * generator is imported anywhere in the webapp any more. Weekdays are ISO 8601 (1=Mon..7=Sun)
 * here as everywhere above the storage layer.
 */

const defaultRecurrenceWeeks = 2;

/** ISO 8601 weekday (1=Mon..7=Sun) of a YYYY-MM-DD date. */
function isoWeekdayOf(date: string): number {
    const jsDay = new Date(date).getDay();

    return jsDay === 0 ? 7 : jsDay;
}

/** What switching "repeats" on offers first: every other week, on the event's own weekday. */
export function defaultRecurrenceRule(date: string): RecurrenceRule {
    return { weeks: defaultRecurrenceWeeks, weekdays: [isoWeekdayOf(date)] };
}

/**
 * Moves the rule with the event. When a series' date changes, the weekday it used to fall on is
 * replaced by the new one and any other selected weekday is left alone, so a weekly Monday series
 * moved to a Wednesday repeats on Wednesdays rather than leaving the event and its rule
 * disagreeing. Returns the rule unchanged when the old weekday was not selected.
 */
export function moveRecurrenceWeekday(
    rule: RecurrenceRule,
    fromDate: string,
    toDate: string,
): RecurrenceRule {
    const from = isoWeekdayOf(fromDate);
    const to = isoWeekdayOf(toDate);

    if (from === to || !rule.weekdays.includes(from)) {
        return rule;
    }

    const weekdays = rule.weekdays.map((day) => (day === from ? to : day));

    return { ...rule, weekdays: [...new Set(weekdays)].toSorted((a, b) => a - b) };
}
